import {useMemo} from "react";
import type {EventContainerTemplate} from "~/domainModels/workspaceModules/eventContainersModule";
import {OttopilotEventContainerStatus} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainer";
import {type UserDefinedObjectTemplateAdditionalColumn, UserDefinedObjectTemplateAdditionalColumnType as ColumnType} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplateAdditionalColumn";
import {formatUserDefinedObjectAdditionalColumnValue} from "~/domainModels/userDefinedObjectAdditionalColumnValues";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {EntityProfileSummaryConfig, EntityProfileSummaryField} from "~/components/entityProfile/types";
import {inferListStatusPillType} from "~/components/ottopilotDataViewer/listStatusConfig";
import {useOptionalGeography} from "~/contexts/geographyContext";
import {useOrganizationalUnits} from "~/contexts/organizationalUnitsContext";
import {
    useEventContainerAdditionalColumns,
    useOrganizationalUnitTemplates,
    usePlatformUserDetails,
    useUserDefinedObjectOptionsByTemplateId,
    useWorkspaceUserTemplates as useUserTemplates,
} from "~/contexts/workspaceContext";
import {useUsers} from "~/contexts/workspaceUsersContext";
import {eventContainerStatusToDisplayName} from "~/utilities/domainStatusDisplayNames";
import {resolveGeographyAdditionalColumnValue} from "~/utilities/geographyValueResolver";
import type {EventContainerWithEvents} from "~/utilities/apis/api";
import {formatDateTime, getUserDisplayName, type PlatformUserDisplayDetails} from "~/utilities/utilities";
import {processOttopilotStringTemplate} from "@vesta/vos-types/modules/workspaceLevel/domain/OttopilotStringTemplate";
import {
    formSectionHasFieldRows,
    parseColumnMappingEntity,
    type ColumnMapping,
    type ColumnMappingEntry,
    type Form,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceUiConfigs/domain/WorkspaceConfig";

function resolveJourneyProfileStatusAppearance(statusLabel: string): NonNullable<EntityProfileSummaryConfig["statusAppearance"]> {
    const pillType = inferListStatusPillType(statusLabel);
    if (pillType === "Archived" || pillType === "Deleted") {
        return "removed";
    }
    if (pillType === "Active") {
        return "success";
    }
    return "default";
}

/**
 * Product status from the event-container template `statusColumnConfig`
 * (e.g. Amaron Lead Status: Live / Converted / Churned). Platform
 * `journey.status` is only Active/Archived and must not be shown as the badge.
 */
function resolveJourneyProfileStatus(args: {
    journey: EventContainerWithEvents;
    eventContainerTemplate: EventContainerTemplate;
    additionalColumns: ReadonlyArray<UserDefinedObjectTemplateAdditionalColumn>;
    userDefinedObjectOptionsByTemplateId: JourneySummaryValueContext["userDefinedObjectOptionsByTemplateId"];
}): Pick<EntityProfileSummaryConfig, "statusLabel" | "statusAppearance"> {
    const statusColumnConfig = args.eventContainerTemplate.configuration.statusColumnConfig;
    if (statusColumnConfig !== undefined) {
        const columnId = statusColumnConfig.columnId;
        const rawValue = args.journey.additionalColumnValues[columnId];
        const colDef = args.additionalColumns.find((column) => column.id === columnId);
        let statusLabel = "-";
        if (colDef !== undefined && colDef.type === ColumnType.userDefinedObject) {
            statusLabel =
                args.userDefinedObjectOptionsByTemplateId
                    .get(colDef.configuration.userDefinedObjectTemplateId)
                    ?.find((option) => option.id === rawValue)?.displayName ?? "-";
        } else if (typeof rawValue === "string" && rawValue.trim() !== "") {
            statusLabel = rawValue.trim();
        }
        if (statusLabel !== "-") {
            return {
                statusLabel,
                statusAppearance: resolveJourneyProfileStatusAppearance(statusLabel),
            };
        }
    }
    return {
        statusLabel: eventContainerStatusToDisplayName(args.journey.status),
        statusAppearance: args.journey.status === OttopilotEventContainerStatus.active ? "success" : "default",
    };
}

function unknownToDisplayString(value: unknown): string {
    if (value === null || value === undefined || value === "") {
        return "-";
    }
    if (typeof value === "string") {
        return value;
    }
    if (typeof value === "number" || typeof value === "boolean") {
        return String(value);
    }
    return "-";
}

type JourneySummaryValueContext = {
    eventContainerTemplate: EventContainerTemplate;
    additionalColumns: ReadonlyArray<UserDefinedObjectTemplateAdditionalColumn>;
    users: ReturnType<typeof useUsers>;
    userTemplates: ReturnType<typeof useUserTemplates>;
    platformUserDetailsByUserId: ReadonlyMap<Uuid, PlatformUserDisplayDetails>;
    organizationalUnits: ReturnType<typeof useOrganizationalUnits>;
    organizationalUnitTemplates: ReturnType<typeof useOrganizationalUnitTemplates>;
    userDefinedObjectOptionsByTemplateId: ReturnType<typeof useUserDefinedObjectOptionsByTemplateId>;
    resolveGeographyValue?: (columnType: string, rawValue: unknown) => string | null;
};

function formatOrganizationalUnitValue(args: {
    ouId: unknown;
    organizationalUnits: ReturnType<typeof useOrganizationalUnits>;
    organizationalUnitTemplates: ReturnType<typeof useOrganizationalUnitTemplates>;
}): {value: string; copyValue?: string} {
    const ouId = args.ouId as Uuid | null | undefined;
    if (ouId === null || ouId === undefined) {
        return {value: "-"};
    }
    const ou = args.organizationalUnits.find((entry) => entry.id === ouId);
    if (ou === undefined) {
        return {value: "-"};
    }
    const ouTemplate = args.organizationalUnitTemplates.find((entry) => entry.id === ou.templateId);
    if (ouTemplate === undefined) {
        return {value: "-"};
    }
    return {
        value: processOttopilotStringTemplate(ouTemplate.instanceDisplayNameTemplate, ou.additionalColumnValues),
    };
}

function formatJourneySummaryFieldValue(args: {
    columnId: Uuid;
    rawValue: unknown;
} & JourneySummaryValueContext): {value: string; copyValue?: string} {
    const colDef = args.additionalColumns.find((column) => column.id === args.columnId);
    if (colDef === undefined) {
        return {value: unknownToDisplayString(args.rawValue)};
    }

    const value = formatUserDefinedObjectAdditionalColumnValue(colDef, args.rawValue, {
        formatTimestamp: formatDateTime,
        referenceOptionsByTemplateId: args.userDefinedObjectOptionsByTemplateId,
        resolveUser: id => getUserDisplayName(id, args.users, args.userTemplates, args.platformUserDetailsByUserId),
        resolveUserTemplate: id => args.userTemplates.find(template => template.id === id)?.displayName ?? "-",
        resolveOrganizationalUnit: id => formatOrganizationalUnitValue({ouId: id, organizationalUnits: args.organizationalUnits, organizationalUnitTemplates: args.organizationalUnitTemplates}).value,
        // The dense profile grid previously hid raw product IDs.
        resolveProduct: () => "-",
        resolveGeography: (type, raw) => args.resolveGeographyValue?.(type, raw) ?? resolveGeographyAdditionalColumnValue(type, raw),
    });
    if (colDef.type === ColumnType.uuid) {
        return {value: /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value) ? "-" : value};
    }
    return colDef.type === ColumnType.indianPhoneNumber && value.trim() !== "" && value !== "-" ? {value, copyValue: value} : {value};
}

function formatFixedJourneyColumn(args: {
    columnName: string;
    journey: EventContainerWithEvents;
    mapping: ColumnMappingEntry;
} & JourneySummaryValueContext): {value: string; copyValue?: string} {
    switch (args.columnName) {
        case "createdAt": {
            return {value: formatDateTime(String(args.journey.createdAt))};
        }
        case "updatedAt": {
            return {value: formatDateTime(String(args.journey.updatedAt))};
        }
        case "createdBy": {
            return {
                value: getUserDisplayName(
                    args.journey.createdBy,
                    args.users,
                    args.userTemplates,
                    args.platformUserDetailsByUserId,
                ),
            };
        }
        case "updatedBy": {
            return {
                value: getUserDisplayName(
                    args.journey.updatedBy,
                    args.users,
                    args.userTemplates,
                    args.platformUserDetailsByUserId,
                ),
            };
        }
        case "status": {
            return {value: eventContainerStatusToDisplayName(args.journey.status)};
        }
        default: {
            return {value: "-"};
        }
    }
}

function resolveSummaryFieldsFromColumnGrouping(args: {
    journey: EventContainerWithEvents;
} & JourneySummaryValueContext): Array<EntityProfileSummaryField> {
    const columnGrouping = args.eventContainerTemplate.configuration.eventContainerDetailsPage.columnGrouping;
    const fields: Array<EntityProfileSummaryField> = [];

    for (const group of columnGrouping) {
        for (const column of group.columns) {
            if (column.columnType !== "OttopilotEventContainer") {
                continue;
            }

            const columnId = column.columnId as Uuid;
            const rawValue = args.journey.additionalColumnValues[columnId];
            const formatted = formatJourneySummaryFieldValue({
                columnId: columnId,
                rawValue: rawValue,
                eventContainerTemplate: args.eventContainerTemplate,
                additionalColumns: args.additionalColumns,
                users: args.users,
                userTemplates: args.userTemplates,
                platformUserDetailsByUserId: args.platformUserDetailsByUserId,
                organizationalUnits: args.organizationalUnits,
                organizationalUnitTemplates: args.organizationalUnitTemplates,
                userDefinedObjectOptionsByTemplateId: args.userDefinedObjectOptionsByTemplateId,
                resolveGeographyValue: args.resolveGeographyValue,
            });

            fields.push({
                id: String(column.id),
                label: column.label,
                value: formatted.value,
                copyValue: formatted.copyValue,
            });
        }
    }

    return fields;
}

function resolveSummaryFieldsFromViewForm(args: {
    journey: EventContainerWithEvents;
    viewForm: Form;
    columnMapping: ColumnMapping;
} & JourneySummaryValueContext): Array<EntityProfileSummaryField> {
    const fields: Array<EntityProfileSummaryField> = [];
    const seenFieldIds = new Set<string>();

    for (const section of args.viewForm.layout.sections) {
        if (formSectionHasFieldRows(section) !== true) {
            continue;
        }
        for (const row of section.fieldRows) {
            for (const formField of row) {
                if (formField.isVisible === false) {
                    continue;
                }
                if (seenFieldIds.has(formField.fieldId) === true) {
                    continue;
                }
                const mapping = args.columnMapping[formField.fieldId];
                if (mapping === undefined) {
                    continue;
                }
                const entity = parseColumnMappingEntity(mapping.entity);
                if (entity === null || entity.kind !== "eventContainer") {
                    continue;
                }

                seenFieldIds.add(formField.fieldId);
                const label = formField.label ?? mapping.displayName;
                const formatted =
                    mapping.type === "fixedColumn"
                        ? formatFixedJourneyColumn({
                              columnName: mapping.columnName,
                              journey: args.journey,
                              mapping: mapping,
                              eventContainerTemplate: args.eventContainerTemplate,
                              additionalColumns: args.additionalColumns,
                              users: args.users,
                              userTemplates: args.userTemplates,
                              platformUserDetailsByUserId: args.platformUserDetailsByUserId,
                              organizationalUnits: args.organizationalUnits,
                              organizationalUnitTemplates: args.organizationalUnitTemplates,
                              userDefinedObjectOptionsByTemplateId: args.userDefinedObjectOptionsByTemplateId,
                              resolveGeographyValue: args.resolveGeographyValue,
                          })
                        : formatJourneySummaryFieldValue({
                              columnId: mapping.columnId,
                              rawValue: args.journey.additionalColumnValues[mapping.columnId],
                              eventContainerTemplate: args.eventContainerTemplate,
                              additionalColumns: args.additionalColumns,
                              users: args.users,
                              userTemplates: args.userTemplates,
                              platformUserDetailsByUserId: args.platformUserDetailsByUserId,
                              organizationalUnits: args.organizationalUnits,
                              organizationalUnitTemplates: args.organizationalUnitTemplates,
                              userDefinedObjectOptionsByTemplateId: args.userDefinedObjectOptionsByTemplateId,
                              resolveGeographyValue: args.resolveGeographyValue,
                          });

                fields.push({
                    id: formField.fieldId,
                    label: label,
                    value: formatted.value,
                    copyValue: formatted.copyValue,
                });
            }
        }
    }

    return fields;
}

export function useJourneyProfileSummaryConfig(args: {
    journey: EventContainerWithEvents;
    eventContainerTemplate: EventContainerTemplate;
    viewForm?: Form;
    columnMapping?: ColumnMapping;
}): EntityProfileSummaryConfig {
    const additionalColumns = useEventContainerAdditionalColumns(args.eventContainerTemplate);
    const users = useUsers();
    const userTemplates = useUserTemplates();
    const platformUserDetails = usePlatformUserDetails();
    const organizationalUnits = useOrganizationalUnits();
    const organizationalUnitTemplates = useOrganizationalUnitTemplates();
    const userDefinedObjectOptionsByTemplateId = useUserDefinedObjectOptionsByTemplateId();
    const geography = useOptionalGeography();

    const platformUserDetailsByUserId = useMemo(
        () => new Map(platformUserDetails.map((detail) => [detail.userId, detail])),
        [platformUserDetails],
    );

    return useMemo(() => {
        const configuration = args.eventContainerTemplate.configuration;
        const title = processOttopilotStringTemplate(configuration.displayNameTemplate, args.journey.additionalColumnValues);

        const callButtonColumnId = configuration.eventContainersPage.callButtonColumnId;
        const phoneRaw =
            callButtonColumnId !== null ? args.journey.additionalColumnValues[callButtonColumnId] : null;
        const phoneNumber = typeof phoneRaw === "string" && phoneRaw.trim() !== "" ? phoneRaw : null;
        const status = resolveJourneyProfileStatus({
            journey: args.journey,
            eventContainerTemplate: args.eventContainerTemplate,
            additionalColumns: additionalColumns,
            userDefinedObjectOptionsByTemplateId: userDefinedObjectOptionsByTemplateId,
        });
        const valueContext: JourneySummaryValueContext = {
            eventContainerTemplate: args.eventContainerTemplate,
            additionalColumns: additionalColumns,
            users: users,
            userTemplates: userTemplates,
            platformUserDetailsByUserId: platformUserDetailsByUserId,
            organizationalUnits: organizationalUnits,
            organizationalUnitTemplates: organizationalUnitTemplates,
            userDefinedObjectOptionsByTemplateId: userDefinedObjectOptionsByTemplateId,
            resolveGeographyValue:
                geography === null ? undefined : geography.resolveAdditionalColumnValue,
        };
        let fields: Array<EntityProfileSummaryField>;
        if (args.viewForm !== undefined && args.columnMapping !== undefined) {
            fields = resolveSummaryFieldsFromViewForm({
                journey: args.journey,
                viewForm: args.viewForm,
                columnMapping: args.columnMapping,
                ...valueContext,
            });
        } else {
            fields = resolveSummaryFieldsFromColumnGrouping({
                journey: args.journey,
                ...valueContext,
            });
        }

        return {
            statusLabel: status.statusLabel,
            statusAppearance: status.statusAppearance,
            title: title,
            phoneNumber: phoneNumber,
            fields: fields,
        };
    }, [
        additionalColumns,
        args.columnMapping,
        args.eventContainerTemplate,
        args.journey,
        args.viewForm,
        geography,
        organizationalUnitTemplates,
        organizationalUnits,
        platformUserDetailsByUserId,
        userTemplates,
        userDefinedObjectOptionsByTemplateId,
        users,
    ]);
}
